'use client';

/**
 * @brisa/ui · components/ui/accordion — shadcn Accordion (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 · adicionado Bruno 2026-05-16
 *
 * Wrapper Radix UI accordion com estilo facelift:
 *   - AccordionItem: border-bottom 1px text-08 (rgba 0,0,0,0.08)
 *   - AccordionTrigger: Archivo Medium 16px, text-100; ChevronDown rotaciona 180° quando open
 *   - AccordionContent: Archivo Regular 14px, text-56; slide down/up via Radix keyframes
 *
 * Figma origem: fileKey 4kRRaXDq85zVVXByHGEFfY · node 191:3738 (Accordion na PassPage)
 *
 * 'use client' obrigatório — Radix Accordion orquestra state interno.
 */
import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

import { cn } from '../../lib/utils';

export const Accordion = AccordionPrimitive.Root;

export const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      // border-bottom 1px text-08 — separador sutil entre itens (Figma 191:3738)
      'border-b border-[hsl(var(--text-08))]',
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

export const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        // group: permite que ChevronDown reaja ao data-state do trigger via group-data-[state=open]
        'group flex w-full items-center justify-between gap-4 py-4 text-left',
        // Tipografia: Archivo Medium 16px, text-100 (Figma 191:3738)
        'font-medium text-base text-[hsl(var(--text-100))]',
        // Transição suave
        'transition-all',
        // a11y
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    >
      {children}
      {/* ChevronDown size 16 — rotaciona 180° quando open (Figma 191:3738) */}
      <ChevronDown
        size={16}
        aria-hidden="true"
        className={cn(
          'shrink-0 text-[hsl(var(--text-100))]',
          // Transição rotate 200ms ease-out — spec Figma
          'transition-transform duration-200 ease-out',
          // group-data-[state=open] reage ao data-state="open" no AccordionTrigger pai
          'group-data-[state=open]:rotate-180',
        )}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

export const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      // Animação slide down/up via Radix keyframes (data-state)
      'overflow-hidden',
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      className,
    )}
    {...props}
  >
    {/* pb-4 + tipografia: Archivo Regular 14px, text-56, line-height ~20px (Figma 191:3738) */}
    <div className="pb-4 text-sm leading-5 text-[hsl(var(--text-56))]">{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
